/**
 * useReservationYears
 *
 * Derives year dropdown options from reservation check-in dates, unioned with the
 * years the daily rate/pricing calendar reliably covers (so owners can check future
 * rates on a property even when nothing is booked yet).
 * Two reservations fetches (backend caps a single request at a 3-year window),
 * no availability chunking, no discovery.
 *
 * - On listingId change: fetch reservations for (currentYear - 2)-01-01 … currentYear-12-31,
 *   and separately for (currentYear + 1)-01-01 … (currentYear + 2)-12-31, to also surface
 *   years from reservations booked out in advance (e.g. Owner Stays).
 * - Extract year from each reservation.check_in_date (or checkin_date).
 * - Always union in currentYear and currentYear + 1: Hostaway's rate/availability sync
 *   is a rolling ~365-day window from today, so those two years always have priced days
 *   regardless of whether any reservation exists yet.
 * - Return unique years across both sources, sorted descending.
 * - On fetch error: log, fallback to [currentYear, currentYear + 1].
 */

import { useState, useEffect, useRef } from 'react';
import { getReservations } from '../services/ownerApi.js';

const debug =
  typeof window !== 'undefined' && new URLSearchParams(window.location.search).get('debug') === '1';

export function useReservationYears({ listingId, propertiesLoading, selectedProperty }) {
  const [years, setYears] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reservationsCount, setReservationsCount] = useState(0);
  const lastListingRef = useRef(null);

  useEffect(() => {
    if (!listingId || propertiesLoading || !selectedProperty) {
      setYears([]);
      setLoading(false);
      setError(null);
      setReservationsCount(0);
      lastListingRef.current = null;
      return;
    }

    if (lastListingRef.current === listingId) return;
    lastListingRef.current = listingId;

    const cy = new Date().getFullYear();
    const backwardRange = { startDate: `${cy - 2}-01-01`, endDate: `${cy}-12-31` };
    const forwardRange = { startDate: `${cy + 1}-01-01`, endDate: `${cy + 2}-12-31` };

    setLoading(true);
    setError(null);
    setYears([]);
    setReservationsCount(0);

    const extractYears = (items, set) => {
      for (const it of items) {
        const d = it.check_in_date ?? it.checkin_date;
        if (!d || typeof d !== 'string') continue;
        const y = new Date(d).getFullYear();
        if (Number.isFinite(y)) set.add(y);
      }
    };

    const fetchYears = async () => {
      try {
        const [backwardResult, forwardResult] = await Promise.allSettled([
          getReservations({ listingId, ...backwardRange }),
          getReservations({ listingId, ...forwardRange }),
        ]);

        if (backwardResult.status === 'rejected') {
          throw backwardResult.reason;
        }
        if (forwardResult.status === 'rejected') {
          console.error('[CalendarPage] useReservationYears forward fetch failed', forwardResult.reason?.message || forwardResult.reason);
        }

        const backwardItems = backwardResult.value.items || backwardResult.value.data?.items || [];
        const forwardItems = forwardResult.status === 'fulfilled'
          ? (forwardResult.value.items || forwardResult.value.data?.items || [])
          : [];
        const set = new Set();
        extractYears(backwardItems, set);
        extractYears(forwardItems, set);
        set.add(cy);
        set.add(cy + 1);
        const derived = [...set].sort((a, b) => b - a);
        const totalCount = backwardItems.length + forwardItems.length;
        setYears(derived);
        setReservationsCount(totalCount);
        setError(null);

        if (debug) {
          console.log('[CalendarPage] DEBUG year-dropdown', {
            source: 'reservations(check_in_date)',
            listingId,
            reservationYears: derived,
            reservationsCount: totalCount,
            dropdownYears: derived,
            timestamp: new Date().toISOString(),
          });
        }
      } catch (err) {
        console.error('[CalendarPage] useReservationYears fetch failed', err?.message || err);
        const fallback = [cy + 1, cy];
        setYears(fallback);
        setReservationsCount(0);
        setError(err?.message || 'Failed to fetch reservation years');

        if (debug) {
          console.log('[CalendarPage] DEBUG year-dropdown (fallback)', {
            source: 'reservations(check_in_date)',
            listingId,
            reservationYears: [],
            reservationsCount: 0,
            dropdownYears: fallback,
            error: err?.message,
            timestamp: new Date().toISOString(),
          });
        }
      } finally {
        setLoading(false);
      }
    };

    fetchYears();
  }, [listingId, propertiesLoading, selectedProperty]);

  return { years, loading, error, reservationsCount };
}
